import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useRouter } from 'next/router';
import {
  BASE_PATHS,
  BREAKPOINTS_WIDTHS,
  debounce,
  palette,
  State,
  template,
  US_STATES,
  useWindowSize,
} from '@belong/common';
import { IconSearch, IconClose, IconArrowDown } from '@belong/icons';
import { Modal, Text, ButtonBase, TextInput } from '@belong/ui';
import clsx from 'clsx';
import { useRegions } from 'src/features/app';
import { useStore } from 'src/store';
import { noRegionPickedSelector } from 'src/store/region';
import { sortStatesByActiveStatus } from 'src/utils/region';
import { StateCard, RegionList } from './state-card';
import { REGION_SELECT_STRINGS as STRINGS } from './strings';

export type From = 'default' | 'listing' | 'homeowners';

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
  from?: From;
};

export function RegionSelect({
  isOpen,
  onDismiss,
  from = 'default',
}: Props): JSX.Element | null {
  const { width } = useWindowSize();

  const { regionsByState, isRegionsLoading } = useRegions();
  const [stateSelected, setStateSelected] = useState<
    State['code'] | undefined
  >();
  const [searchDebounced, setSearchDebounced] = useState('');
  const [search, setSearch] = useState('');
  const noRegionPicked = useStore(noRegionPickedSelector);
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);

  const isMobile = width < BREAKPOINTS_WIDTHS.MD;
  /* In mobile we have two steps to select the region
    First we select the state and then the region
  */
  const showMobileSelectRegionScreen = isMobile && !!stateSelected;

  // Scroll to top when entering the select region screen
  useEffect(() => {
    if (showMobileSelectRegionScreen) {
      modalRef.current?.scrollTo(0, 0);
    }
  }, [showMobileSelectRegionScreen]);

  // States list is sorted by:
  // 1. States with active status regions
  // 2. Inactive states
  const sortedActiveStates = sortStatesByActiveStatus(regionsByState);

  // TODO: improve and document this functionality.
  const filteredStates = sortedActiveStates
    .filter(({ state, status }) =>
      from === 'listing' ? status === 'Active' : state,
    )
    .filter(
      ({ state }) =>
        US_STATES[state].name.toLowerCase().includes(searchDebounced) ||
        US_STATES[state].name === searchDebounced ||
        regionsByState[state].some((region) =>
          region.region?.toLowerCase().includes(searchDebounced),
        ),
    );

  // 3. Nation wide

  if (from !== 'listing' || !filteredStates.length) {
    filteredStates.push({ state: 'NA', status: 'Active' });
  }

  function handleStateClick(state: State['code']) {
    return () => {
      if (state === 'NA') {
        handleDismiss();
        noRegionPicked();
        // TODO: Think if should handle in the same way
        if (from === 'homeowners') {
          router.push(BASE_PATHS.HOMEOWNER);
        }
      } else {
        setStateSelected(state);
      }
    };
  }

  function handleResetSearch() {
    setSearch('');
    setSearchDebounced('');
  }

  function handleDismiss() {
    setStateSelected(undefined);
    handleResetSearch();
    onDismiss();
  }

  const debouncedSearchInputChange = debounce(setSearchDebounced, 300);

  function onSearchInputChange(event: ChangeEvent<HTMLInputElement>) {
    debouncedSearchInputChange(event.target.value.toLowerCase());
    setSearch(event.target.value);
  }

  if (isRegionsLoading) {
    return null;
  }

  const handleCloseSelectRegionScreen = () => {
    setStateSelected(undefined);
  };

  return (
    <Modal
      size="fullScreen"
      isOpen={isOpen}
      onDismiss={handleDismiss}
      name="region_select"
      ref={modalRef}
    >
      {showMobileSelectRegionScreen && (
        // Show "Return btn" in mobile to return to the state selection page
        <ButtonBase
          className="left-xs top-xs p-xs md:left-lg md:top-sm absolute block rotate-90"
          onClick={handleCloseSelectRegionScreen}
        >
          <IconArrowDown width={16} fill={palette.navy} />
        </ButtonBase>
      )}
      <div className="pb-2xl pt-3xl container flex w-full flex-col items-center justify-center md:h-full">
        <div className="mb-2xl">
          <Text
            variant="h2"
            fontWeight="semibold"
            className="whitespace-pre-line text-center"
          >
            {showMobileSelectRegionScreen && stateSelected
              ? template(MODAL_TITLE_WITH_STATE_MAP[from], {
                  state: US_STATES[stateSelected].name,
                })
              : MODAL_TITLE_MAP[from]}
          </Text>
        </div>
        {!showMobileSelectRegionScreen && (
          <div className="mb-xl w-full sm:hidden">
            <TextInput
              placeholder={STRINGS['search']}
              value={search}
              label=" "
              focused={false}
              RightElement={
                search !== '' ? (
                  <IconClose width={20} onClick={handleResetSearch} />
                ) : (
                  <IconSearch width={20} />
                )
              }
              onChange={onSearchInputChange}
            />
          </div>
        )}
        <div
          className={clsx(
            'gap-sm md:gap-lg grid w-full auto-cols-min grid-flow-row-dense grid-cols-2 grid-rows-none sm:w-auto sm:grid-cols-3',
            {
              'md:grid-cols-4': filteredStates.length > 3,
              'xl:grid-cols-5': filteredStates.length === 4,
              'xl:grid-cols-6': filteredStates.length > 5,
            },
          )}
        >
          {/* In mobile after we select the state we only show the list of regions */}
          {showMobileSelectRegionScreen ? (
            <RegionList
              code={stateSelected}
              onDismiss={handleDismiss}
              from={from}
              name={US_STATES[stateSelected].name}
              withDescription={false}
            />
          ) : (
            filteredStates.map(({ state, status }) => (
              <StateCard
                code={US_STATES[state].code}
                name={US_STATES[state].name}
                status={status || 'ComingSoon'}
                src={`/modals/region_select/v5/region_icon_${US_STATES[state].code.toLowerCase()}_normal.svg`}
                hoverSrc={`/modals/region_select/v5/region_icon_${US_STATES[state].code.toLowerCase()}_selected.svg`}
                onClick={handleStateClick(state)}
                key={state}
                selected={
                  stateSelected === US_STATES[state].code ||
                  US_STATES[state].name.toLowerCase() === searchDebounced ||
                  regionsByState[state]?.some(
                    (region) =>
                      region.region?.toLowerCase() === searchDebounced,
                  )
                }
                from={from}
                onDismiss={handleDismiss}
                search={searchDebounced}
              />
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

const MODAL_TITLE_MAP: Record<From, string> = {
  default: STRINGS['title.default'],
  homeowners: STRINGS['title.default'],
  listing: STRINGS['title.listing'],
};

const MODAL_TITLE_WITH_STATE_MAP: Record<From, string> = {
  default: STRINGS['title.defaultWithState'],
  homeowners: STRINGS['title.defaultWithState'],
  listing: STRINGS['title.listingWithState'],
};
